import { Component, OnInit, HostListener } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  PlanRow,
  FEATURE_ROWS,
} from "../subscription/subscription-data.component";

@Component({
  selector: "app-public-subscription",
  templateUrl: "./public-subscription.component.html",
  styleUrls: ["./public-subscription.component.scss"],
  standalone: false,
})
export class PublicSubscriptionComponent implements OnInit {
  isYearly = false;
  isMobile = false;
  isKeyFeaturesExpanded = false;
  mobileKeyFeaturesExpanded: { [planName: string]: boolean } = {};
  selectedCurrency = "USD";
  availableCurrencies = ["USD", "EUR", "PLN"];
  isLoaded = false;

  constructor(private translateService: TranslateService) {}

  featureRows = FEATURE_ROWS;

  trackByRow(index: number, row: PlanRow): string {
    return row.feature;
  }

  ngOnInit() {
    this.checkScreenSize();
    this.initializeLanguage();
    this.initializeIframeCommunication();
  }

  @HostListener("window:resize", ["$event"])
  onResize = () => {
    this.checkScreenSize();
  };

  checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
  }

  initializeLanguage() {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");

    const currentLang =
      langParam && ["pl", "es", "en"].includes(langParam) ? langParam : "en";

    this.translateService.use(currentLang);
    this.setCurrencyByLanguage(currentLang);
  }

  setCurrencyByLanguage(lang: string): void {
    switch (lang) {
      case "pl":
        this.selectedCurrency = "PLN";
        break;
      case "es":
        this.selectedCurrency = "EUR";
        break;
      case "en":
      default:
        this.selectedCurrency = "USD";
        break;
    }
  }

  toggleKeyFeatures() {
    this.isKeyFeaturesExpanded = !this.isKeyFeaturesExpanded;
  }

  toggleMobileKeyFeatures(planName: string) {
    this.mobileKeyFeaturesExpanded[planName] =
      !this.mobileKeyFeaturesExpanded[planName];
  }

  shouldShowRow(row: PlanRow, index: number): boolean {
    if (row.feature === "subscription.keyFeaturesInDetail") {
      return !this.isKeyFeaturesExpanded;
    }

    const keyFeaturesIndex = this.featureRows.findIndex(
      (r) => r.feature === "subscription.keyFeaturesInDetail",
    );
    if (index > keyFeaturesIndex) {
      return this.isKeyFeaturesExpanded;
    }

    return true;
  }

  shouldShowMobileRow(row: PlanRow, index: number, planName: string): boolean {
    if (row.feature === "subscription.keyFeaturesInDetail") {
      return !this.mobileKeyFeaturesExpanded[planName];
    }

    const keyFeaturesIndex = this.featureRows.findIndex(
      (r) => r.feature === "subscription.keyFeaturesInDetail",
    );
    if (index > keyFeaturesIndex) {
      return this.mobileKeyFeaturesExpanded[planName];
    }

    return true;
  }

  subscribeToPlan(planName: string) {
    const signUpUrl = "https://gsignature.com/sign-up";

    const params = new URLSearchParams({
      plan: planName.toLowerCase(),
      subscriptionType: this.isYearly ? "yearly" : "monthly",
    });

    const fullUrl = `${signUpUrl}?${params.toString()}`;

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        {
          type: "NAVIGATE",
          url: fullUrl,
        },
        "*",
      );
    } else {
      window.location.href = fullUrl;
    }
  }

  scrollTable(direction: "left" | "right") {
    const container = document.querySelector(".desktop-view");
    if (container) {
      container.scrollBy({
        left: direction === "left" ? -220 : 220,
        behavior: "smooth",
      });
    }
  }

  canScroll(direction: "left" | "right"): boolean {
    const container = document.querySelector(".desktop-view");
    if (!container) {
      return false;
    }

    if (direction === "left") {
      return container.scrollLeft > 0;
    } else {
      const maxScroll = container.scrollWidth - container.clientWidth;
      return container.scrollLeft < maxScroll - 1;
    }
  }

  getPlanPrice(planName: string): string {
    const prices = this.getBillingPlanPrices(planName, this.selectedCurrency);
    const price = this.isYearly ? prices.yearly : prices.monthly;
    return `${price} ${this.selectedCurrency}`;
  }

  private getBillingPlanPrices(
    planName: string | null | undefined,
    currency: string = "USD",
  ): { monthly: number; yearly: number } {
    const isPLN = currency.toUpperCase() === "PLN";

    switch (planName?.toLowerCase()) {
      case "freemium":
      case "free":
        return { monthly: 0, yearly: 0 };
      case "basic":
        return { monthly: isPLN ? 3.99 : 0.99, yearly: isPLN ? 3.59 : 0.89 };
      case "premium":
        return { monthly: isPLN ? 7.99 : 1.99, yearly: isPLN ? 7.19 : 1.79 };
      default:
        return { monthly: 0, yearly: 0 };
    }
  }

  getPlanDescription(planName: string): string {
    const planKey = planName.toLowerCase();
    return this.translateService.instant(
      `subscription.planDescriptions.${planKey}`,
    ) as string;
  }

  getTranslatedValue(value: string): string {
    if (value === "yes" || value === "" || value === "-") {
      return value;
    }
    if (value.startsWith("subscription.")) {
      return this.translateService.instant(value) as string;
    }
    return value;
  }

  onCurrencyChange(newCurrency: string) {
    this.selectedCurrency = newCurrency;
  }

  private initializeIframeCommunication(): void {
    this.sendHeightToParent();

    window.addEventListener("resize", () => this.sendHeightToParent());

    const observer = new MutationObserver(() => {
      this.sendHeightToParent();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  private sendHeightToParent(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const height = document.body.scrollHeight;

        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            {
              type: "RESIZE_IFRAME",
              height,
            },
            "*",
          );
        }

        window.postMessage(
          {
            type: "RESIZE_IFRAME",
            height,
          },
          "*",
        );
      });
    });
  }
}
